import { TeamType } from "@redotech/account-common/team-type";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { assertNever } from "@redotech/util/type";
import { useContext, useMemo } from "react";
import { AssignedFulfillmentServicesContext } from "../services/assigned-fulfillment-service";

export enum GroupsAssignedState {
  MyStore = "myStore",
  DifferentFulfillmentService = "differentFulfillmentService",
  MyFulfillmentService = "myFulfillmentService",
  Mixed = "mixed",
}

export const useFulfillmentGroupPermissions = (
  fulfillmentGroups: readonly FulfillmentGroupData[],
) => {
  const linkedFulfillmentServices = useContext(
    AssignedFulfillmentServicesContext,
  );

  const team = useContext(TeamContext);

  const allAssignedToTeam = useMemo(() => {
    return fulfillmentGroups.every(
      (group) =>
        !group.assignedFulfillmentService ||
        (team?.teamType !== TeamType.FulfillmentService &&
          group.assignedFulfillmentService._id === team?._id.toString()),
    );
  }, [fulfillmentGroups, team]);

  const assignedToOtherFulfillmentService = useMemo(() => {
    if (allAssignedToTeam) {
      return false;
    }

    if (linkedFulfillmentServices.length > 0) {
      const linkedIds = new Set(
        linkedFulfillmentServices.map((fs) => fs._id.toString()),
      );
      return fulfillmentGroups.every(
        (group) =>
          group.assignedFulfillmentService?._id !== undefined &&
          linkedIds.has(group.assignedFulfillmentService._id),
      );
    }

    return false;
  }, [fulfillmentGroups, linkedFulfillmentServices, allAssignedToTeam]);

  const assignedToMyFulfillmentService = useMemo(() => {
    if (!team || team.teamType !== TeamType.FulfillmentService) {
      return false;
    }

    return fulfillmentGroups.every(
      (group) => group.assignedFulfillmentService?._id === team._id.toString(),
    );
  }, [fulfillmentGroups, team]);

  const groupsAssignedState = useMemo<GroupsAssignedState>(() => {
    if (allAssignedToTeam) {
      return GroupsAssignedState.MyStore;
    }

    if (assignedToMyFulfillmentService) {
      return GroupsAssignedState.MyFulfillmentService;
    }

    if (assignedToOtherFulfillmentService) {
      return GroupsAssignedState.DifferentFulfillmentService;
    }

    return GroupsAssignedState.Mixed;
  }, [
    allAssignedToTeam,
    assignedToOtherFulfillmentService,
    assignedToMyFulfillmentService,
  ]);

  const permissionsToEditEveryGroup = useMemo(() => {
    switch (groupsAssignedState) {
      case GroupsAssignedState.MyStore:
        return true;
      case GroupsAssignedState.MyFulfillmentService:
        return true;
      case GroupsAssignedState.DifferentFulfillmentService:
        return false;
      case GroupsAssignedState.Mixed:
        return false;
      default:
        assertNever(groupsAssignedState);
    }
  }, [groupsAssignedState]);

  return {
    linkedFulfillmentServices,
    groupsAssignedState,
    permissionsToEditEveryGroup,
  };
};
